import { useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { normalizeBlueprintVersion } from '../config/itemTypeConfig';
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";
import { useGlossaryBlueprintsDataContext } from '@/providers/GlossaryBlueprintsDataProvider';
import { useDocumentDataContext } from '@/providers/DocumentDataProvider';
import { useDialogDataContext } from '@/providers/DialogDataProvider';
import { useTypesDataContext } from '@/providers/TypesDataProvider';

interface UseItemMoveToFolderProps {
  selectedGame: any;
}

export function useItemMoveToFolder({ selectedGame }: UseItemMoveToFolderProps) {
  const { toast } = useToast();
  
  // Get data contexts
  const { glossaryItems } = useGlossaryDataContext();
  const { glossaryBlueprints } = useGlossaryBlueprintsDataContext();
  const { documents } = useDocumentDataContext();
  const { dialogs } = useDialogDataContext();
  const { types } = useTypesDataContext();

  // Global event listener for moving items to folders from anywhere in the app
  useEffect(() => {
    const handleMoveItemToFolder = async (event: CustomEvent) => {
      const { itemId, folderId, gameId, itemType } = event.detail;
      
      console.log('[useItemMoveToFolder] Received moveItemToFolder event:', { itemId, folderId, gameId, itemType });
      
      if (!selectedGame?.id || gameId !== selectedGame.id) {
        console.log('[useItemMoveToFolder] Skipping - game mismatch:', { selectedGameId: selectedGame?.id, eventGameId: gameId });
        return;
      }
      
      try {
        if (itemType === "blueprints") {
          const item = glossaryBlueprints?.find((bp: any) => bp.id === itemId);
          if (!item) {
            console.error("Blueprint not found:", itemId);
            return;
          }

          const { updateTemplate } = await import("@/lib/stores/templatesStore");
          const normalizedVersion = normalizeBlueprintVersion(item.version);
          
          await updateTemplate({
            id: item.id,
            game_id: item.game_id,
            name: item.name,
            tags: item.tags,
            type: item.type || "glossary",
            description: item.description,
            data: item.data as any,
            settings: item.settings,
            version: normalizedVersion,
            folder: folderId,
          });

          toast({
            title: "Success",
            description: folderId ? `"${item.name}" moved to folder` : `"${item.name}" removed from folder`,
          });

        } else if (itemType === "library") {
          const item = glossaryItems?.find((gi: any) => gi.id === itemId);
          if (!item) {
            console.error("Glossary item not found:", itemId);
            return;
          }

          const { updateObject, fetchObjects } = await import("@/lib/stores/objectsStore");
          
          await updateObject({
            id: item.id,
            game_id: item.game_id,
            name: item.name,
            type: item.type || "glossary",
            data: item.data,
            tags: item.tags,
            blueprint_id: item.blueprint_id,
            version: item.version,
            blueprint_version: item.blueprint_version || 0,
            identifiers: item.identifiers,
            folder: folderId, // Use the target folderId from the drop event, not item.folderId
          }, 'patch', undefined, toast);

          await fetchObjects(selectedGame.id);

          toast({
            title: "Success",
            description: folderId ? `"${item.name}" moved to folder` : `"${item.name}" removed from folder`,
          });

        } else if (itemType === "documents") {
          const item = documents?.find((doc: any) => doc.id === itemId);
          if (!item) {
            console.error("Document not found:", itemId);
            return;
          }

          console.log('[useItemMoveToFolder] Updating document with folderId:', folderId, 'item:', item.name);
          
          const { updateDocumentItem, fetchDocumentItems } = await import("@/lib/stores/documentStore");
          await updateDocumentItem({
            id: item.id,
            game_id: item.game_id,
            name: item.name,
            data: item.data,
            settings: item.settings,
            tags: item.tags,
            folder: folderId,
          });

          await fetchDocumentItems(selectedGame.id);

          toast({
            title: "Success",
            description: folderId ? `"${item.name}" moved to folder` : `"${item.name}" removed from folder`,
          });

        } else if (itemType === "dialogs") {
          const item = dialogs?.find((d: any) => d.id === itemId);
          if (!item) {
            console.error("Dialog not found:", itemId);
            return;
          }

          const { updateDialog, fetchDialogs } = await import("@/lib/stores/dialogStore");
          
          await updateDialog({
            id: item.id,
            game_id: item.game_id,
            name: item.name,
            dialog_key: item.dialog_key,
            description: item.description,
            tags: item.tags,
            status: item.status,
            data: item.data,
            folder: folderId,
          });

          await fetchDialogs(selectedGame.id);

          toast({
            title: "Success",
            description: folderId ? `"${item.name}" moved to folder` : `"${item.name}" removed from folder`,
          });

        } else if (itemType === "variables") {
          const item = types?.find((t: any) => t.id === itemId);
          if (!item) {
            console.error("Variable not found:", itemId);
            return;
          }

          const { updateType, fetchTypes } = await import("@/lib/stores/typesStore");
          
          await updateType({
            id: item.id,
            game_id: item.game_id,
            name: item.name,
            element_type: item.element_type,
            group: item.group_name,
            description: item.description,
            data: item.data || [],
            folder: folderId,
          }, toast);

          await fetchTypes(selectedGame.id);

          toast({
            title: "Success",
            description: folderId ? `"${item.name}" moved to folder` : `"${item.name}" removed from folder`,
          });
        }
      } catch (error) {
        console.error("Failed to move item to folder:", error);
        toast({
          title: "Error",
          description: "Failed to move item to folder",
          variant: "destructive",
        });
      }
    };

    window.addEventListener("moveItemToFolder", handleMoveItemToFolder as EventListener);
    return () => {
      window.removeEventListener("moveItemToFolder", handleMoveItemToFolder as EventListener);
    };
  }, [selectedGame, glossaryItems, glossaryBlueprints, documents, dialogs, types, toast]);
}
